import React from 'react';
import { AlertCircle, ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import {
  CHECKLIST_TABLE_MAX_COLUMNS,
  CHECKLIST_TABLE_MAX_ROWS,
  checklistTableConfigError,
  normalizeChecklistTableConfig,
  type ChecklistTableColumn,
  type ChecklistTableColumnType,
} from '../../leads/utils/checklistTable';
import { SETTINGS_INPUT_CLASS, SETTINGS_SELECT_TRIGGER_CLASS } from '../utils/settingsSectionStyles';

const COLUMN_TYPE_OPTIONS: { value: ChecklistTableColumnType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'choice', label: 'Auswahl' },
  { value: 'employee', label: 'Mitarbeiter' },
];

function newColumn(): ChecklistTableColumn {
  return { key: `c_${Math.random().toString(36).slice(2, 10)}`, label: '', type: 'text' };
}

/**
 * Columns of a checklist `table` question: heading, type (text, number with unit, date, choice with
 * options, employee), required flag, order. Rows are added by the technician in the app.
 */
export function ChecklistTableColumnsEditor({
  config,
  showErrors,
  onConfigChange,
}: {
  config: Record<string, unknown> | undefined;
  showErrors: boolean;
  onConfigChange: (patch: Record<string, unknown>) => void;
}) {
  const columns: ChecklistTableColumn[] = Array.isArray(config?.columns)
    ? (config!.columns as ChecklistTableColumn[])
    : [];
  const maxRows = config?.max_rows != null && String(config.max_rows).trim() !== '' ? String(config.max_rows) : '';
  const error = showErrors ? checklistTableConfigError(normalizeChecklistTableConfig(config)) : null;

  const setColumns = (next: ChecklistTableColumn[]) => onConfigChange({ columns: next });
  const updateColumn = (index: number, patch: Partial<ChecklistTableColumn>) =>
    setColumns(columns.map((col, i) => (i === index ? { ...col, ...patch } : col)));
  const moveColumn = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= columns.length) return;
    const next = [...columns];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setColumns(next);
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-slate-400">Spalten</div>
      {columns.map((column, index) => (
        <div key={column.key} className="group/col flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={column.label}
            onChange={(e) => updateColumn(index, { label: e.target.value })}
            placeholder="Spaltenüberschrift …"
            className={`${SETTINGS_INPUT_CLASS} min-w-[140px] flex-1`}
          />
          <AppSelectDropdown
            value={column.type}
            onChange={(value) => {
              const type = value as ChecklistTableColumnType;
              updateColumn(index, {
                type,
                unit: type === 'number' ? column.unit : undefined,
                options: type === 'choice' ? (column.options ?? ['', '']) : undefined,
              });
            }}
            options={COLUMN_TYPE_OPTIONS}
            className="w-[130px]"
            triggerClassName={`${SETTINGS_SELECT_TRIGGER_CLASS} !text-[13px]`}
            chevronClassName="!text-slate-400"
          />
          {column.type === 'number' ? (
            <input
              type="text"
              value={column.unit ?? ''}
              onChange={(e) => updateColumn(index, { unit: e.target.value })}
              placeholder="Einheit, z. B. Std."
              className={`${SETTINGS_INPUT_CLASS} w-[130px]`}
            />
          ) : null}
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs font-medium text-app-text-muted">
            <input
              type="checkbox"
              checked={column.required === true}
              onChange={(e) => updateColumn(index, { required: e.target.checked || undefined })}
              className="h-3.5 w-3.5 rounded border-app-border-strong accent-pipedrive-blue"
            />
            Pflicht
          </label>
          <div className="flex items-center opacity-60 transition-opacity group-hover/col:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={() => moveColumn(index, -1)}
              disabled={index === 0}
              title="Nach vorne"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-app-hover disabled:opacity-30"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => moveColumn(index, 1)}
              disabled={index === columns.length - 1}
              title="Nach hinten"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-app-hover disabled:opacity-30"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setColumns(columns.filter((_, i) => i !== index))}
              disabled={columns.length <= 1}
              title="Spalte entfernen"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {column.type === 'choice' ? (
            <input
              type="text"
              value={(column.options ?? []).join(', ')}
              onChange={(e) => updateColumn(index, { options: e.target.value.split(',').map((o) => o.trimStart()) })}
              placeholder="Optionen, durch Komma getrennt (z. B. Ja, Nein)"
              className={`${SETTINGS_INPUT_CLASS} ml-4 w-[calc(100%-1rem)]`}
            />
          ) : null}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => setColumns([...columns, newColumn()])}
          disabled={columns.length >= CHECKLIST_TABLE_MAX_COLUMNS}
          className="inline-flex items-center gap-1 text-xs font-semibold text-pipedrive-blue hover:underline disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Spalte hinzufügen
        </button>
        <div className="flex shrink-0 items-center gap-1.5">
          <label className="whitespace-nowrap text-xs font-medium text-slate-400">Max. Zeilen</label>
          <input
            type="text"
            inputMode="numeric"
            value={maxRows}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d]/g, '');
              onConfigChange({
                max_rows: raw ? Math.min(Math.max(Number(raw), 1), CHECKLIST_TABLE_MAX_ROWS) : undefined,
              });
            }}
            placeholder={String(CHECKLIST_TABLE_MAX_ROWS)}
            className={`${SETTINGS_INPUT_CLASS} !w-[72px] shrink-0`}
          />
        </div>
      </div>
      {error ? (
        <p className="flex items-center gap-1.5 text-xs font-medium text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
